<template>
    <!-- Small screens -->
    <div class="container-fluid px-0 d-md-none">
        <!-- Nav  -->
        <div class="d-flex align-items-center justify-content-between small-nav">
            <!-- Back icon -->
            <a class="navbar-brand fw-bold fs-4 py-0 m-0 cursor-pointer rounded-circle" v-load-page="'/'"
                :data-do-load="!tabOpen" @click="closeTab($event)" v-ripple>
                <i class="bi-arrow-left"></i>
            </a>
            <!-- Title -->
            <p ref="smTitle" class="fs-5 fw-bold m-0">Privacy Policy</p>
            <!-- Menu toggler -->
            <i class="cursor-unset"></i>
        </div>
        <!-- Menu -->
        <nav ref="smMenu" class="navbar small-nav p-0 double-adjust">
            <div class="d-flex flex-column w-100">
                <div class="flex-grow p-0">
                    <ul class="navbar-nav mt-3">
                        <template v-for="page in privacyPages">
                            <li class="nav-item px-3 m-block-n1" @click="showTab(page)" v-ripple>
                                <a class="p-big dropdown-item px-0 d-flex align-items-center cursor-pointer">
                                    <i class="cat-nav-icon me-3 w-auto" :class="page.iconClass"></i>
                                    {{ page.label }}
                                    <i class="ms-auto pe-0 w-auto" :class="page.iconRightClass"></i>
                                </a>
                            </li>
                            <li class="nav-item mx-3">
                                <hr class="dropdown-divider">
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
        </nav>
        <!-- Content -->
        <div class="mt-3 py-2 d-none" ref="legalContentSectionSm">
            <div v-html="content">
            </div>
        </div>
    </div>

    <!-- Large screens -->
    <div class="container-fluid px-0 d-none d-md-block">
        <!-- Breadcrumbs (Large screens) -->
        <nav aria-label="breadcrumb" class="d-none d-md-flex mt-3">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <router-link to="/" class="text-decoration-none">
                        Home
                    </router-link>
                </li>
                <li class="breadcrumb-item active" aria-current="page">Privacy Policy</li>
            </ol>
        </nav>

        <!-- Title -->
        <h2 class="fw-bold text-start mb-large">Privacy Policy</h2>

        <div class="divider sm"></div>

        <div class="row m-0">
            <!-- Menu -->
            <div class="col-4 col-md-5 col-lg-4 col-xl-3 p-0 mw-400">
                <div class="d-flex flex-column w-100">
                    <div class="flex-grow p-0">
                        <ul class="navbar-nav" role="tablist">
                            <template v-for="(page, index) in privacyPages">
                                <li class="nav-item pe-3 m-block-n1" role="presentation" v-ripple>
                                    <a class="dropdown-item p-0 d-flex align-items-center cursor-pointer"
                                        :class="index == 0 ? 'active' : ''" @click="showTab(page)" data-bs-toggle="tab"
                                        :data-bs-target="`#privacy-${index}`" role="tab" type="button">
                                        <i class="ps-0 me-3" :class="page.iconClass"></i>
                                        {{ page.label }}
                                        <i class="ms-auto px-0 ms-auto w-auto move-right" :class="page.iconRightClass"></i>
                                    </a>
                                </li>
                                <li class="nav-item pe-3">
                                    <hr class="dropdown-divider">
                                </li>
                            </template>
                        </ul>
                    </div>
                </div>
            </div>
            <!-- Content -->
            <div class="col-8 col-md-7 col-lg-8 col-xl-9 border-start p-3 min-vh-100" ref="legalContentSectionLg">
                <div class="tab-content" id="nav-tabContent">
                    <div class="tab-pane" :class="index == 0 ? 'show active' : ''" :id="`privacy-${index}`" role="tabpanel"
                        tabindex="0" v-for="(page, index) in privacyPages">
                        <div v-html="content">
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import apiClient from '@/utils/axios.js';
import { useHttpErrorManager } from '@/composables/useHttpErrorManager.js';
import { useAppHelpers } from '@/composables/useAppHelpers.js';

const helper = useAppHelpers();

const props = defineProps({
    slug: String
});

const smTitle = ref(null);
const smMenu = ref(null);
const legalContentSectionSm = ref(null);
const tabOpen = ref(false);
const currPage = ref(null);
const legalContentSectionLg = ref(null);

const privacyPages = [
    {
        iconClass: 'bi-shield-check',
        label: 'Privacy Policy',
        iconRightClass: 'bi-chevron-right ms-auto',
        slug: 'privacy_policy'
    },
    {
        iconClass: 'bi-cookie',
        label: 'Cookie Policy',
        iconRightClass: 'bi-chevron-right ms-auto',
        slug: 'cookie_policy'
    },
    {
        iconClass: 'bi-database-lock',
        label: 'Data Subject Policy',
        iconRightClass: 'bi-chevron-right ms-auto',
        slug: 'data_subject_policy'
    },
];

const content = ref('');
const title = ref('');
const contentSlug = ref(props.slug ? props.slug : 'privacy_policy');

const loadContent = async () => {
    if (contentSlug.value) {
        helper.loaderOn(legalContentSectionSm.value);
        helper.loaderOn(legalContentSectionLg.value);
        let url = "/company/legal/" + contentSlug.value;

        try {
            const response = await apiClient.get(url, {});
            if (response.status === 200) {
                content.value = response.data.page.content
                title.value = response.data.page.title
            }
            helper.loaderOff(legalContentSectionSm.value);
            helper.loaderOff(legalContentSectionLg.value);
        } catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
            helper.loaderOff(legalContentSectionSm.value);
            helper.loaderOff(legalContentSectionLg.value);
        }
    }
}

onMounted(() => {
    loadContent();
});

watch(contentSlug, (val) => {
    loadContent();
})

const showTab = (page) => {
    contentSlug.value = page.slug;

    setTimeout(() => {
        tabOpen.value = true;
        currPage.value = page.label;
        smTitle.value.innerText = page.label;
        smMenu.value.classList.add("d-none");
        legalContentSectionSm.value.classList.remove("d-none");
    }, 500)
}

const closeTab = (event) => {
    if (!tabOpen.value) return;

    setTimeout(() => {
        tabOpen.value = false;
        currPage.value = null;
        smTitle.value.innerText = "Privacy Policy";
        smMenu.value.classList.remove("d-none");
        legalContentSectionSm.value.classList.add("d-none");
    }, 500)
};

</script>

<style scoped lang="scss">
.double-adjust {
    margin: 0 -16px;
    margin-top: 10px;
}

.dropdown-divider {
    margin: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.175);
}

.dropdown-item {
    &:not(.p-big) {
        padding-bottom: 0.7em !important;
        padding-top: 0.7em !important;
    }

    &:hover {
        background-color: transparent !important;

        .move-right {
            transform: translateX(5px);
        }
    }

    .move-right {
        transition: transform 0.3s linear;
    }
}


.title {
    margin-top: 3.4rem;
    margin-bottom: 1em;
}

.mw-400 {
    max-width: 400px;
}

.nav-item.ripple {
    @media (min-width: 768px) {
        margin-left: -24px;
        padding-left: 24px;
    }

    @media (min-width: 1399px) {
        margin-left: -32px;
        padding-left: 32px;
    }
}
</style>